import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { Eye, EyeOff, Loader2, Pencil, Check, X, Repeat } from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TOKEN_KEY = "mra_jwt";
const client = axios.create({ baseURL: API, withCredentials: true });
client.interceptors.request.use((cfg) => {
  const t = typeof window !== "undefined" ? localStorage.getItem(TOKEN_KEY) : null;
  if (t) {
    cfg.headers = cfg.headers || {};
    cfg.headers.Authorization = `Bearer ${t}`;
  }
  return cfg;
});
const fmt$ = (c) => `$${(c / 100).toLocaleString(undefined, { minimumFractionDigits: 0 })}`;

const RollRow = ({ d, onSaved }) => {
  const [editing, setEditing] = useState(false);
  const [name, setName] = useState(d.roll.display_name || "");
  const [city, setCity] = useState(d.roll.city || "");
  const [busy, setBusy] = useState(false);

  const patch = async (body, okMsg) => {
    setBusy(true);
    try {
      const r = await client.patch(`/admin/sustaining-donors/${d.donation_id}/roll`, body);
      onSaved(r.data);
      toast.success(okMsg);
      setEditing(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't save that change.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <li className="px-5 py-3" data-testid={`roll-row-${d.donation_id}`}>
      <div className="flex items-start justify-between gap-4">
        <div className="min-w-0">
          <p className="truncate font-medium text-ink">
            {d.donor_name || d.donor_email || d.donation_id}
            <span className="ml-2 text-xs text-ink/50">{fmt$(d.amount_cents)}/mo · since {d.since}</span>
          </p>
          <p className="mt-0.5 text-xs text-ink/60" data-testid={`roll-state-${d.donation_id}`}>
            {d.roll.opt_in ? (
              <>On the roll as <span className="font-semibold text-ink">{d.roll.display_name || "Anonymous"}</span>{d.roll.city ? ` · ${d.roll.city}` : ""}</>
            ) : (
              <>Not shown on the roll{d.roll.display_name ? ` (name on file: ${d.roll.display_name})` : ""}</>
            )}
            {d.sustaining_note_sent_at && !d.sustaining_note_error && <span className="ml-2 text-grass">· month-two note sent</span>}
            {d.sustaining_note_error && <span className="ml-2 text-scarlet" title={d.sustaining_note_error}>· month-two note failed to send</span>}
          </p>
        </div>
        <div className="flex shrink-0 items-center gap-1.5">
          <button
            type="button"
            disabled={busy}
            onClick={() => patch({ opt_in: !d.roll.opt_in }, d.roll.opt_in ? "Hidden from the roll" : "Shown on the roll")}
            data-testid={`roll-toggle-${d.donation_id}`}
            title={d.roll.opt_in ? "Hide from roll" : "Show on roll"}
            className="inline-flex h-8 w-8 items-center justify-center rounded-full border border-ink/15 text-ink hover:bg-ink hover:text-sand disabled:opacity-50"
          >
            {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : d.roll.opt_in ? <EyeOff className="h-3.5 w-3.5" strokeWidth={1.75} /> : <Eye className="h-3.5 w-3.5" strokeWidth={1.75} />}
          </button>
          <button
            type="button"
            onClick={() => setEditing((v) => !v)}
            data-testid={`roll-edit-${d.donation_id}`}
            title="Rename"
            className="inline-flex h-8 w-8 items-center justify-center rounded-full border border-ink/15 text-ink hover:bg-ink hover:text-sand"
          >
            <Pencil className="h-3.5 w-3.5" strokeWidth={1.75} />
          </button>
        </div>
      </div>
      {editing && (
        <form
          onSubmit={(e) => { e.preventDefault(); patch({ display_name: name, city }, "Roll entry updated"); }}
          className="mt-3 flex flex-wrap items-center gap-2"
          data-testid={`roll-edit-form-${d.donation_id}`}
        >
          <input value={name} onChange={(e) => setName(e.target.value)} maxLength={60} placeholder="Name shown on the roll" data-testid={`roll-name-input-${d.donation_id}`} className="min-w-[180px] flex-1 rounded-md border border-ink/15 bg-bone px-3 py-1.5 text-sm text-ink focus:border-ink focus:outline-none" />
          <input value={city} onChange={(e) => setCity(e.target.value)} maxLength={60} placeholder="City (optional)" data-testid={`roll-city-input-${d.donation_id}`} className="min-w-[140px] rounded-md border border-ink/15 bg-bone px-3 py-1.5 text-sm text-ink focus:border-ink focus:outline-none" />
          <button type="submit" disabled={busy} data-testid={`roll-save-${d.donation_id}`} className="inline-flex h-8 items-center gap-1 rounded-full bg-ink px-3 text-xs font-semibold text-sand disabled:opacity-50"><Check className="h-3.5 w-3.5" /> Save</button>
          <button type="button" onClick={() => setEditing(false)} className="inline-flex h-8 w-8 items-center justify-center rounded-full border border-ink/15 text-ink"><X className="h-3.5 w-3.5" /></button>
        </form>
      )}
    </li>
  );
};

/** Admin panel: active monthly donors and their public roll entry (hide / show / rename). */
export const SustainingRollPanel = () => {
  const [rows, setRows] = useState(null);
  const load = useCallback(async () => {
    try {
      const r = await client.get("/admin/sustaining-donors");
      setRows(r.data);
    } catch {
      setRows([]);
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  const onSaved = (updated) => setRows((prev) => prev.map((r) => (r.donation_id === updated.donation_id ? updated : r)));
  const shown = rows?.filter((r) => r.roll.opt_in).length ?? 0;

  return (
    <div className="rounded-lg border border-ink/10 bg-sand" data-testid="sustaining-roll-panel">
      <div className="flex items-center justify-between border-b border-ink/10 px-5 py-4">
        <div>
          <p className="font-serif text-lg text-ink">Sustaining donor roll</p>
          <p className="mt-0.5 text-xs text-ink/60">
            Active monthly donors. {rows ? `${shown} of ${rows.length} shown on the homepage.` : "Loading…"} Names only, never amounts.
          </p>
        </div>
        <Repeat className="h-4 w-4 text-ink/40" strokeWidth={1.75} />
      </div>
      <ul className="divide-y divide-ink/10">
        {rows === null ? (
          <li className="px-6 py-8 text-center text-xs text-ink/60"><Loader2 className="mx-auto h-4 w-4 animate-spin" /></li>
        ) : rows.length === 0 ? (
          <li className="px-6 py-8 text-center" data-testid="sustaining-roll-empty">
            <p className="font-serif text-lg text-ink">No monthly donors yet.</p>
            <p className="mt-1 text-xs text-ink/60">Sustaining donors will appear here the moment their first monthly gift is paid.</p>
          </li>
        ) : (
          rows.map((d) => <RollRow key={d.donation_id} d={d} onSaved={onSaved} />)
        )}
      </ul>
    </div>
  );
};
